import { Box, GlobalStyles } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import SYSTEM_FONT_FAMILY from "constants/fontFamily";
import { ReactNode } from "react";
import createAppTheme from "theme";

interface PublicThemeProviderProps {
  children: ReactNode;
}

const PUBLIC_FONT_SCOPE = "[data-public-font-root]";
const PUBLIC_FORM_ELEMENTS_SELECTOR = [
  `${PUBLIC_FONT_SCOPE} input`,
  `${PUBLIC_FONT_SCOPE} textarea`,
  `${PUBLIC_FONT_SCOPE} button`,
  `${PUBLIC_FONT_SCOPE} select`,
].join(", ");

const typographyVariants = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2",
  "button",
  "caption",
  "overline",
  "h40",
  "h34",
  "h28",
  "h24m",
  "h24rg",
  "h24",
  "h20",
  "text",
  "subtitle18rg",
  "subtitle18lg",
  "body16",
  "body16rg",
  "body16Extra",
  "body14rg",
  "describe",
  "describe2",
  "colored",
] as const;

const baseTheme = createAppTheme();
const publicTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: SYSTEM_FONT_FAMILY,
    ...Object.fromEntries(
      typographyVariants.map((variant) => [
        variant,
        {
          ...baseTheme.typography[variant],
          fontFamily: SYSTEM_FONT_FAMILY,
        },
      ])
    ),
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: SYSTEM_FONT_FAMILY,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: SYSTEM_FONT_FAMILY,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: SYSTEM_FONT_FAMILY,
        },
        input: {
          fontFamily: SYSTEM_FONT_FAMILY,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: SYSTEM_FONT_FAMILY,
        },
      },
    },
  },
});

const PublicThemeProvider = ({ children }: PublicThemeProviderProps) => {
  return (
    <ThemeProvider theme={publicTheme}>
      <GlobalStyles
        styles={{
          [`${PUBLIC_FONT_SCOPE}, ${PUBLIC_FONT_SCOPE} *`]: {
            fontFamily: `${SYSTEM_FONT_FAMILY} !important`,
          },
          [PUBLIC_FORM_ELEMENTS_SELECTOR]: {
            fontFamily: `${SYSTEM_FONT_FAMILY} !important`,
          },
        }}
      />
      <Box
        data-public-font-root
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          fontFamily: SYSTEM_FONT_FAMILY,
        }}>
        {children}
      </Box>
    </ThemeProvider>
  );
};

export default PublicThemeProvider;
